import { normalize } from 'normalizr';

import { ThunkActionCreator } from 'app/store';
import { post, get, put } from 'utils/api';
import { ContentType } from 'models/common';
import { Course, courseSchema } from 'models/course';
import { Bundle, bundleSchema } from 'models/bundle';
import { Action } from 'components/organisms/publish/interfaces';
import { getSelectedOrganizationIdCookies } from 'utils/cookies';
import { selectOrganization } from 'slices/organizations';
import { fetchCourseDone } from 'slices/courses';
import { fetchBundleDone } from 'slices/bundles';
import { addRecords } from 'slices/entities';

export interface Document {
  id: string;
  type: ContentType;
  parentId?: string;
}

interface CreatePackagePayload {
  courseId: string;
  documents: Document[];
  documentsForTracking: Document[];
  action: Action;
}

export const createPackage: ThunkActionCreator<CreatePackagePayload> = ({
  courseId,
  documents,
  documentsForTracking,
  action,
}) => async () => {
  const res = await post(`/publishing/packages`, {
    course_id: courseId,
    documents,
    documents_for_tracking: documentsForTracking,
    isPublishing: action === Action.Publish || action === Action.QuickSend,
    publish_action: action,
  });

  if (res.status >= 400) {
    throw res.status;
  } else {
    return res.json();
  }
};

export const fetchPackage: ThunkActionCreator<{ id: string }> = ({
  id,
}) => async () => {
  const res = await get(`/publishing/packages/${id}`);

  if (res.status >= 400) {
    throw res.status;
  } else {
    return res.json();
  }
};

export const publishPackage: ThunkActionCreator<{ id: string }> = ({
  id,
}) => async () => {
  const res = await put(`/publishing/packages/${id}/publish`);

  if (res.status >= 400) {
    throw res.status;
  } else {
    return res.json();
  }
};

export const unpublishPackage: ThunkActionCreator<{ id: string }> = ({
  id,
}) => async () => {
  const res = await put(`/publishing/packages/${id}/unpublish`);

  if (res.status >= 400) {
    throw res.status;
  } else {
    return res.json();
  }
};

export const fetchCourse: ThunkActionCreator<{
  id: string;
  setOrg?: boolean;
}> = ({ id, setOrg = false }) => async dispatch => {
  const res = await get(`/publishing/courses/${id}`);

  if (res.status >= 400) {
    throw res.status;
  } else {
    const course: Course = await res.json();
    const normalizedData = normalize<Course>(course, courseSchema);
    dispatch(addRecords(normalizedData));

    const orgId = course.organization_id;

    if (setOrg && orgId && orgId !== getSelectedOrganizationIdCookies()) {
      dispatch(selectOrganization(course.organization_id));
    }

    dispatch(fetchCourseDone());
  }
};

export const fetchBundle: ThunkActionCreator<{
  id: string;
  setOrg?: boolean;
}> = ({ id, setOrg = false }) => async dispatch => {
  const res = await get(`/publishing/bundles/${id}`);

  if (res.status >= 400) {
    throw res.status;
  } else {
    const bundle: Bundle = await res.json();
    const normalizedData = normalize<Bundle>(bundle, bundleSchema);
    dispatch(addRecords(normalizedData));

    const orgId = bundle.organization_id;

    if (setOrg && orgId && orgId !== getSelectedOrganizationIdCookies()) {
      dispatch(selectOrganization(orgId));
    }

    dispatch(fetchBundleDone());
  }
};

export const fetchTopic: ThunkActionCreator<{
  id: string;
}> = ({ id }) => async () => {
  const res = await get(`/publishing/topics/${id}`);

  if (res.status >= 400) {
    throw res.status;
  }

  return res.json();
};
